import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Min, Scale, Subtract, Total } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign } from "@pdfking/renderer-api"

import { MONTH_LAYOUT, PALETTE } from "#constants"
import type { AgendaDesign } from "#internal"

import { DrawMonthGrid } from "../calendar/DrawMonthGrid"
import { MonthName } from "../calendar/MonthName"
import { ShiftMonth } from "../calendar/ShiftMonth"
import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { Slice } from "../layout/Slice"
import { CreateMeasure } from "../text/CreateMeasure"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"
import { AgendaDate } from "./AgendaDate"
import { AgendaWeeks } from "./AgendaWeeks"
import { DrawAgendaDay } from "./DrawAgendaDay"

const MEASURE = CreateMeasure()

// The hours a day is ruled by, from 8 to 23, and the rows Sunday's header
// takes between Saturday's and its own
const HOURS = 16
const SUNDAY = 6
// The months either side's share of the notes' width, tall
const MONTHS = 0.45

/** The right page of week `week` of the agenda of `year` as Papyrus has it,
 * within `frame`, `pitch` its lines apart: on its left half Friday by the
 * hours, Saturday over Sunday beside it, each by the hours; on its right half
 * the week's month and year, its notes dotted under them and the months either
 * side at their foot. Its names in `language`. */
export const DrawAgendaWeekNotes = ({
  design: { badge },
  frame,
  language,
  pitch,
  week,
  year,
}: {
  design: AgendaDesign
  frame: SpatialRectangle<Distance>
  language: string
  pitch: Distance
  week: number
  year: number
}): PageShape[] => {
  const { first } = AgendaWeeks(year)
  const date = (weekday: number) => AgendaDate(year, first + 7 * week + weekday)
  const gap = Scale(pitch, 0.5)
  const [days, side] = Slice.left(frame, Scale(Subtract(frame.width, gap), 0.5), gap)
  const [friday, weekend] = Slice.grid(days, 2, 1, gap)
  // One ruling under the headers: Friday two rows an hour, a dotted line on
  // its halves, Saturday and Sunday one, Sunday under Saturday's past its
  // header's rows
  const header = Scale(pitch, 1.5)
  const top = Total(days.y, days.height)
  const row = Scale(Subtract(days.height, header), 1 / (2 * HOURS + SUNDAY))
  const saturday = {
    ...weekend!,
    height: Total(header, Scale(row, HOURS)),
    y: Subtract(top, Total(header, Scale(row, HOURS))),
  }
  const sunday = { ...weekend!, height: Total(header, Scale(row, HOURS)), y: days.y }
  const day = (box: SpatialRectangle<Distance>, weekday: number, hour: Distance) =>
    DrawAgendaDay({
      badge,
      box,
      date: date(weekday),
      hours: true,
      language,
      month: false,
      pitch,
      ruling: { halves: hour > row, hour, hours: HOURS },
      weekday,
    })
  // The week's month, its Thursday's, over the notes; the months either side
  // at their foot
  const thursday = date(3)
  const title = `${MonthName(thursday.month, language)} ${thursday.year}`.toUpperCase()
  // a line and a half high, or as wide as the notes fit
  const font = Min(Scale(pitch, 1.2), Scale(pitch, side.width / MEASURE(title, pitch)))
  const [head, rest] = Slice.top(side, Scale(pitch, 2), gap)
  const [months, notes] = Slice.bottom(rest, Scale(side.width, MONTHS), gap)
  return [
    ...day(friday!, 4, Scale(row, 2)),
    ...day(saturday, 5, row),
    ...day(sunday, 6, row),
    PlainText(
      title,
      head.x,
      head.width,
      Total(head.y, Scale(Subtract(head.height, font), 0.5)),
      font,
      PALETTE.primary,
      HorizontalAlign.RIGHT
    ),
    ...PlotNotebookBackground({ background: NotebookBackground.DOTTED_LINES, box: notes, pitch }),
    ...Slice.grid(months, 2, 1, gap).flatMap((box, i) => {
      const shifted = ShiftMonth(thursday.year, thursday.month, i ? 1 : -1)
      return DrawMonthGrid({
        box,
        language,
        layout: { ...MONTH_LAYOUT, muted: false, rules: false, title: "month_year" },
        month: shifted.month,
        year: shifted.year,
      })
    }),
  ]
}
